import { ReactNode } from 'react';

import { Chip, Box, Typography, SxProps, Theme } from '@mui/material';

import { AnimatedCollapse } from './AnimatedCollapse';
import { BadgeCircle } from './BadgeCircle';

type BadgeProps = Readonly<{
  children: ReactNode;
  badgeName: string;
  title: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}>;

export const BadgeGroup = ({ children, badgeName, title, isOpen, setIsOpen }: BadgeProps) => {
  return (
    <Box sx={containerSx(isOpen)}>
      <Box sx={badgeContainerSx(isOpen)} onClick={() => setIsOpen(!isOpen)}>
        <Chip
          variant="badge"
          label={badgeName}
          size="md"
          icon={<BadgeCircle />}
          sx={chipSx}
          type="leading"
        />
        <Typography variant="text-sm-medium">{title}</Typography>
      </Box>
      <Box sx={collapseContainerSx}>
        <AnimatedCollapse isOpen={isOpen} springFactor={1.2} key={badgeName}>
          {children}
        </AnimatedCollapse>
      </Box>
    </Box>
  );
};

const containerSx =
  (isOpen: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    cursor: 'pointer',
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    minHeight: isOpen ? '56px' : 'auto',
    height: { xs: 'fit-content', md: 'max-content' },
    borderRadius: `${backrs.radius[isOpen ? 'radius-none' : 'radius-md']} ${backrs.radius['radius-md']} ${backrs.radius['radius-md']} ${backrs.radius['radius-md']}`,
    border: `1px solid ${backrs.colors['Gray/800']}`,
    '&:hover': {
      boxShadow: backrs.shadows['shadow-md'],
      transform: 'translate(-2px, -1px)',
    },
    transition: 'all 0.3s ease-in-out',
    flex: 'none',
  });

const badgeContainerSx =
  (isOpen: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    userSelect: 'none',
    backgroundColor: backrs.colors['Colors/Base/White'],
    top: 0,
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    alignItems: 'center',
    borderRadius: backrs.spacing['spacing-md'],
    padding: !isOpen
      ? `${backrs.spacing['spacing-sm']} ${backrs.spacing['spacing-sm']} ${backrs.spacing['spacing-sm']} ${backrs.spacing['spacing-sm']}`
      : `${backrs.spacing['spacing-sm']} ${backrs.spacing['spacing-sm']}`,
    gap: backrs.spacing['spacing-md'],
    transition: 'padding 0.3s ease-in-out',
  });

const chipSx: SxProps<Theme> = ({ backrs }) => ({
  '& .MuiChip-icon': {
    marginLeft: backrs.spacing['spacing-none'],
    marginRight: backrs.spacing['spacing-none'],
  },
});

const collapseContainerSx: SxProps<Theme> = {
  minHeight: 0,
};
